import * as React from 'react'
import { Ban, Check, Copy, KeyRound, Plus, Shield, Terminal, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate, relativeTime } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

interface AccessToken {
  id: string
  user_id: string
  label: string
  prefix: string
  created_at: string
  expires_at: string
  last_used_at: string | null
  revoked_at: string | null
}

const LIFETIMES = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
]

/**
 * Personal access tokens for the MCP server and scripts/team.sh.
 *
 * The token is generated HERE, in the browser, and only its SHA-256 reaches the
 * database — so this page is the one and only moment the plaintext exists
 * anywhere. That's why it's shown once, loudly, with a copy button: there is no
 * "show it again" to fall back on, by design rather than by omission.
 */
export function AccessPage() {
  const { session, isAdmin } = useAuth()
  const userId = session?.user?.id
  const [creating, setCreating] = React.useState(false)
  const [issued, setIssued] = React.useState<{ token: string; label: string } | null>(null)
  const [busy, setBusy] = React.useState<string | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!userId) return { mine: [], others: [], owners: new Map<string, string>() }

    // Explicitly scoped to this user: a DPL admin's SELECT policy covers every
    // row, so without the filter an admin's "your tokens" list would quietly
    // include everyone else's.
    const mine = await sb()
      .from('mcp_tokens')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    if (mine.error) throw mine.error

    if (!isAdmin) {
      return { mine: mine.data as AccessToken[], others: [], owners: new Map<string, string>() }
    }

    const [all, profiles] = await Promise.all([
      sb().from('mcp_tokens').select('*').neq('user_id', userId).order('created_at', { ascending: false }),
      sb().from('profiles').select('user_id, email'),
    ])
    if (all.error) throw all.error
    if (profiles.error) throw profiles.error

    return {
      mine: mine.data as AccessToken[],
      others: all.data as AccessToken[],
      owners: new Map((profiles.data ?? []).map((p) => [p.user_id as string, p.email as string])),
    }
  }, [userId, isAdmin])

  async function revoke(token: AccessToken) {
    setBusy(token.id)
    setActionError(null)
    const { error: err } = await sb()
      .from('mcp_tokens')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', token.id)
    setBusy(null)
    if (err) return setActionError(err.message)
    void refetch()
  }

  async function remove(token: AccessToken) {
    if (!window.confirm(`Delete “${token.label}”? Anything using it loses access immediately.`)) return
    setBusy(token.id)
    setActionError(null)
    const { error: err } = await sb().from('mcp_tokens').delete().eq('id', token.id)
    setBusy(null)
    if (err) return setActionError(err.message)
    void refetch()
  }

  return (
    <Page
      title="CLI & Claude access"
      description="Access tokens that let Claude and scripts/team.sh configure teams as you. Don’t Panic Labs staff only."
      actions={
        <Button onClick={() => setCreating(true)}>
          <Plus /> New token
        </Button>
      }
    >
      {error && <ErrorNote error={error} />}
      {actionError && <ErrorNote error={actionError} />}

      <Card className="mb-6 border-primary/30 bg-primary/5 shadow-none">
        <CardContent className="flex items-start gap-3 py-4">
          <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="text-sm">
            <p className="font-medium">What these are for</p>
            <p className="mt-0.5 text-muted-foreground">
              This portal has no passwords, which leaves command-line tools with nothing to sign in
              with. A token is the stand-in: paste one into the MCP server or{' '}
              <span className="font-mono text-xs text-foreground">scripts/team.sh</span> and it acts{' '}
              <span className="font-medium text-foreground">as you</span> — same teams, same
              permissions, nothing more. Revoke it here at any time.
            </p>
            <p className="mt-2 text-muted-foreground">
              Limited to <span className="font-medium text-foreground">@dontpaniclabs.com</span>{' '}
              accounts. The database enforces it, at both mint and sign-in — so a token stops
              working if its owner stops being staff.
            </p>
          </div>
        </CardContent>
      </Card>

      {issued && <IssuedToken issued={issued} onDismiss={() => setIssued(null)} />}

      {loading ? (
        <Loading />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Your tokens</CardTitle>
              <CardDescription>
                {data && data.mine.length > 0
                  ? 'A token stops working the moment it is revoked or expires.'
                  : 'None yet.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!data || data.mine.length === 0 ? (
                <EmptyState
                  icon={KeyRound}
                  title="No access tokens"
                  description="Create one to let Claude or team.sh configure teams on your behalf."
                />
              ) : (
                <div className="divide-y">
                  {data.mine.map((t) => (
                    <TokenRow
                      key={t.id}
                      token={t}
                      busy={busy === t.id}
                      onRevoke={() => void revoke(t)}
                      onDelete={() => void remove(t)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {isAdmin && data && data.others.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="h-4 w-4 text-primary" /> Everyone else’s tokens
                </CardTitle>
                <CardDescription>
                  Visible to Don’t Panic Labs staff. You can revoke or delete these — useful when
                  someone rolls off a team — but you cannot create one as someone else.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {data.others.map((t) => (
                    <TokenRow
                      key={t.id}
                      token={t}
                      owner={data.owners.get(t.user_id) ?? 'unknown account'}
                      busy={busy === t.id}
                      onRevoke={() => void revoke(t)}
                      onDelete={() => void remove(t)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {creating && (
        <CreateTokenDialog
          onClose={() => setCreating(false)}
          onIssued={(token, label) => {
            setCreating(false)
            setIssued({ token, label })
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

function statusOf(token: AccessToken): { label: string; tone: 'ok' | 'gone' } {
  if (token.revoked_at) return { label: 'Revoked', tone: 'gone' }
  if (new Date(token.expires_at).getTime() <= Date.now()) return { label: 'Expired', tone: 'gone' }
  return { label: 'Active', tone: 'ok' }
}

function TokenRow({
  token,
  owner,
  busy,
  onRevoke,
  onDelete,
}: {
  token: AccessToken
  owner?: string
  busy: boolean
  onRevoke: () => void
  onDelete: () => void
}) {
  const status = statusOf(token)
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 first:pt-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{token.label}</span>
          <Badge variant={status.tone === 'ok' ? 'secondary' : 'outline'} className="text-[10px]">
            {status.label}
          </Badge>
        </div>
        <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{token.prefix}…</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {owner && <span className="text-foreground">{owner} · </span>}
          created {formatDate(token.created_at)} ·{' '}
          {token.last_used_at ? `last used ${relativeTime(token.last_used_at)}` : 'never used'} ·{' '}
          {status.tone === 'gone' ? 'expired' : 'expires'} {formatDate(token.expires_at)}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {status.tone === 'ok' && (
          <Button variant="ghost" size="sm" onClick={onRevoke} disabled={busy}>
            <Ban /> Revoke
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          disabled={busy}
          aria-label={`Delete ${token.label}`}
        >
          <Trash2 />
        </Button>
      </div>
    </div>
  )
}

/**
 * The generated token, shown once.
 *
 * It comes with the two commands someone actually needs next, because a bare
 * secret with no instructions is where this feature would otherwise end: people
 * copy it, don't know where it goes, and generate three more.
 */
function IssuedToken({
  issued,
  onDismiss,
}: {
  issued: { token: string; label: string }
  onDismiss: () => void
}) {
  return (
    <Card className="mb-6 border-primary">
      <CardHeader>
        <CardTitle className="text-base">Copy “{issued.label}” now</CardTitle>
        <CardDescription>
          This is the only time it will be shown — only a hash of it is stored, so it cannot be
          recovered. If you lose it, delete it and make another.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <CopyBox value={issued.token} mono />
        <Separator />
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium">For the CLI</p>
            <p className="mb-1.5 text-xs text-muted-foreground">
              Run this once; it is remembered for future commands.
            </p>
            <CopyBox value={`./scripts/team.sh login ${issued.token}`} mono />
          </div>
          <div>
            <p className="font-medium">For Claude</p>
            <p className="text-xs text-muted-foreground">
              Paste the token into the conversation and ask Claude to sign in with it — it will call{' '}
              <span className="font-mono">sign_in_with_token</span>. Or set{' '}
              <span className="font-mono">TAP_TOKEN</span> in the environment Claude launches from.
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onDismiss}>
            I’ve copied it
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function CopyBox({ value, mono = false }: { value: string; mono?: boolean }) {
  const [copied, setCopied] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setFailed(false)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused (permissions, insecure context). Say so,
      // rather than showing a "Copied" that didn't happen — the value is
      // selectable, so a manual copy still works.
      setFailed(true)
    }
  }

  return (
    <div>
      <div className="flex items-stretch gap-2">
        <code
          className={`min-w-0 flex-1 select-all overflow-x-auto whitespace-nowrap rounded-md border bg-muted/50 px-3 py-2 text-xs ${
            mono ? 'font-mono' : ''
          }`}
        >
          {value}
        </code>
        <Button variant="outline" size="sm" onClick={() => void copy()} className="shrink-0">
          {copied ? <Check /> : <Copy />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      {failed && (
        <p className="mt-1 text-xs text-muted-foreground">
          Couldn’t reach the clipboard — select the text above and copy it manually.
        </p>
      )}
    </div>
  )
}

function CreateTokenDialog({
  onClose,
  onIssued,
}: {
  onClose: () => void
  onIssued: (token: string, label: string) => void
}) {
  const [label, setLabel] = React.useState('')
  const [days, setDays] = React.useState('90')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const guard = useDiscardGuard(useDirty({ label, days }), onClose)

  async function create(e: React.FormEvent) {
    e.preventDefault()
    const name = label.trim()
    if (!name) return setError('Give the token a name so you can recognise it later.')

    setBusy(true)
    setError(null)
    try {
      const token = await generateToken()
      const expires = new Date(Date.now() + Number(days) * 86_400_000).toISOString()
      const { error: err } = await sb()
        .from('mcp_tokens')
        .insert({
          label: name,
          prefix: token.slice(0, 12),
          token_hash: await sha256Hex(token),
          expires_at: expires,
        })
      if (err) throw err
      onIssued(token, name)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-md">
        <DiscardPrompt guard={guard} what="this token" />
        <form onSubmit={create}>
          <DialogHeader>
            <DialogTitle>New access token</DialogTitle>
            <DialogDescription>
              It will act as you, with exactly your permissions. Name it after the machine or tool
              that will hold it.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="label">Name</Label>
              <Input
                id="label"
                autoFocus
                maxLength={80}
                placeholder="Claude on my laptop"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiry">Expires</Label>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger id="expiry">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LIFETIMES.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Every token expires. A credential nobody remembers issuing is the one that outlives
                the laptop it was on.
              </p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Generating…' : 'Generate token'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 32 random bytes, base64url, prefixed so it's recognisable in a shell history
 * or a config file. Generated in the browser and never sent anywhere: only its
 * hash goes to the database.
 */
async function generateToken(): Promise<string> {
  if (!globalThis.crypto?.getRandomValues || !globalThis.crypto?.subtle) {
    throw new Error(
      'This browser will not provide cryptographic randomness on an insecure connection. ' +
        'Open the portal over HTTPS (or on localhost) and try again.',
    )
  }
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  const b64 = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return `tap_${b64}`
}

async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
